import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { branchApi } from '../../api';
import { RootState } from '..';
import {
  Branch,
  CreateBranch,
  IGroup,
  UpdateBranch,
  CoverageRadiusPayload,
  IBusinessHours,
} from '../../types';
import { toast } from 'react-toastify';

interface BranchState {
  loading: boolean;
  branches: Branch[];
  branchesByBrand: Branch[];
  branchMenu: { name: string; groups: IGroup[] } | null;
}

const initialState: BranchState = {
  loading: false,
  branches: [],
  branchesByBrand: [],
  branchMenu: null,
};

export const branchSlice = createSlice({
  name: 'branch',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllBranches.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllBranches.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.branches = payload;
    });
    builder.addCase(getAllBranches.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getBranchesByBrandId.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBranchesByBrandId.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.branchesByBrand = payload;
    });
    builder.addCase(getBranchesByBrandId.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(createBranch.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createBranch.fulfilled, (state) => {
      state.loading = false;
      toast.success('Branch created successfully');
    });
    builder.addCase(createBranch.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateCoverageRadius.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCoverageRadius.fulfilled, (state) => {
      state.loading = false;
      toast.success('Coverage radius updated successfully');
    });
    builder.addCase(updateCoverageRadius.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(removeCoverageRadius.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeCoverageRadius.fulfilled, (state) => {
      state.loading = false;
      toast.success('Coverage radius removed successfully');
    });
    builder.addCase(removeCoverageRadius.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getBranchMenu.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBranchMenu.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.branchMenu = payload;
    });
    builder.addCase(getBranchMenu.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateBranch.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateBranch.fulfilled, (state) => {
      state.loading = false;
      toast.success('Branch updated successfully');
    });
    builder.addCase(updateBranch.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateBranchName.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateBranchName.fulfilled, (state) => {
      state.loading = false;
      toast.success('Branch name updated successfully');
    });
    builder.addCase(updateBranchName.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateBranchBusinessHours.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateBranchBusinessHours.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateBranchBusinessHours.rejected, (state) => {
      state.loading = false;
    });
  },
});

const getAllBranches = createAsyncThunk('getAllBranches', async () => {
  try {
    const { data } = await branchApi.getAllBranches();
    return data?.data;
  } catch (error) {
    return [];
  }
});

const getBranchesByBrandId = createAsyncThunk(
  'getBranchesByBrandId',
  async (brandId: number | string) => {
    try {
      const { data } = await branchApi.getBranchesByBrandId(brandId);
      return data?.data;
    } catch (error) {
      return [];
    }
  }
);

const createBranch = createAsyncThunk(
  'createBranch',
  async (params: { payload: CreateBranch; cb: () => void }) => {
    try {
      const { payload, cb } = params;
      const { data } = await branchApi.createBranch(payload);
      if (payload.coverageRadius) {
        await branchApi.updateCoverageRadius({
          brandId: payload.brandId,
          kitchenId: payload.kitchenId,
          coverageRadius: payload.coverageRadius,
        });
      }
      cb();
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

const updateCoverageRadius = createAsyncThunk(
  'updateCoverageRadius',
  async (params: { payload: CoverageRadiusPayload; cb?: () => void }) => {
    try {
      const { payload, cb } = params;
      const { data } = await branchApi.updateCoverageRadius(payload);
      cb && cb();
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

const removeCoverageRadius = createAsyncThunk(
  'removeCoverageRadius',
  async (params: {
    payload: Omit<CoverageRadiusPayload, 'coverageRadius'>;
    cb?: () => void;
  }) => {
    try {
      const { payload, cb } = params;
      const { data } = await branchApi.removeCoverageRadius(payload as any);
      cb && cb();
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

const getBranchMenu = createAsyncThunk(
  'getBranchMenu',
  async (param: string) => {
    try {
      const { data } = await branchApi.getBranchMenu(param);
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

const updateBranch = createAsyncThunk(
  'updateBranch',
  async (params: { payload: UpdateBranch; id: string; cb?: () => void }) => {
    try {
      const { payload, id, cb } = params;
      const { data } = await branchApi.updateBranch(payload, id);
      cb && cb();
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

const updateBranchName = createAsyncThunk(
  'updateBranchName',
  async (params: {
    payload: { name: string };
    id: string;
    cb?: () => void;
  }) => {
    try {
      const { payload, id, cb } = params;
      const { data } = await branchApi.updateBranchName(payload, id);
      cb && cb();
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

const updateBranchBusinessHours = createAsyncThunk(
  'updateBranchBusinessHours',
  async (params: {
    payload: IBusinessHours;
    branchId: string | number;
    cb?: () => void;
  }) => {
    try {
      const { payload, branchId, cb } = params;
      const { data } = await branchApi.updateBranchBusinessHours(
        payload,
        branchId
      );
      cb && cb();
      return data?.data;
    } catch (error) {
      return null;
    }
  }
);

export const branchSelector = (state: RootState) => state.branch;
export {
  getAllBranches,
  createBranch,
  getBranchMenu,
  updateBranch,
  updateBranchName,
  getBranchesByBrandId,
  updateCoverageRadius,
  removeCoverageRadius,
  updateBranchBusinessHours,
};
export default branchSlice.reducer;
